import { getCompetition, nextId } from "../core/access.js";
import { CONTINENTAL_ID, GROUP_ROUNDS, buildContinentalGroups, continentalKnockoutStages, } from "./continental.js";
import { emptyStandings } from "./standings.js";
import { buildStateFixtures, stateRounds } from "./state-cup.js";
/**
 * Calendário da temporada.
 *
 * Cada rodada global é um dia de jogo e nenhum clube joga duas vezes na mesma
 * rodada. As rodadas são repartidas entre liga, copa nacional e competição
 * continental, e cada tipo pega dias espalhados pela temporada, como o meio de
 * semana e as datas internacionais no futebol de verdade.
 *
 * A repartição é global, não por país: a continental atravessa fronteiras, e
 * dar a ela dias próprios em toda parte é o que garante que ninguém acumule
 * dois jogos no mesmo dia.
 */
/** Tabela de turno e returno pelo método do círculo. */
export function doubleRoundRobin(clubIds, rng) {
    const teams = rng.shuffle([...clubIds]);
    if (teams.length % 2 === 1)
        teams.push("__BYE__");
    const size = teams.length;
    const roundsPerHalf = size - 1;
    const half = size / 2;
    const wheel = [...teams];
    const firstHalf = [];
    for (let round = 0; round < roundsPerHalf; round++) {
        const pairs = [];
        for (let i = 0; i < half; i++) {
            const a = wheel[i];
            const b = wheel[size - 1 - i];
            if (a === "__BYE__" || b === "__BYE__")
                continue;
            // Alterna o mando para ninguém ficar com jogos em casa demais seguidos.
            pairs.push((round + i) % 2 === 0 ? [a, b] : [b, a]);
        }
        firstHalf.push(pairs);
        const moved = wheel.pop();
        wheel.splice(1, 0, moved);
    }
    const secondHalf = firstHalf.map((pairs) => pairs.map(([home, away]) => [away, home]));
    return [...firstHalf, ...secondHalf];
}
/**
 * A chave de cada fase, pela quantidade de clubes que a disputam.
 *
 * É identificador, não texto: vai para `Fixture.stage` e para `CupStage.key`, e
 * é por ele que a partida encontra a fase. Chave de uma potência de dois que não
 * esteja na tabela vira `fase256`, que a tela escreve pelo número: acontece só
 * em banco de dados com mais de 128 clubes num país, e inventar um nome para
 * cada tamanho seria inventar nome que ninguém usa.
 */
const STAGE_KEYS = {
    2: "final",
    4: "semifinal",
    8: "quartas",
    16: "oitavas",
    32: "segunda",
    64: "primeira",
    128: "inicial",
};
/** A chave de uma fase de mata-mata pelo número de clubes que entram nela. */
export function cupStageKey(size) {
    return STAGE_KEYS[size] ?? `fase${size}`;
}
/** A fase que abre uma copa grande demais para o chaveamento fechar redondo. */
export const PRELIMINARY_STAGE = "preliminar";
/** A fase que decide o título, a única em campo neutro. */
export const FINAL_STAGE = "final";
/** Fases de uma copa mata-mata a partir do número de clubes inscritos. */
export function buildCupStages(clubCount) {
    const stages = [];
    let bracket = 1;
    while (bracket * 2 <= clubCount)
        bracket *= 2;
    if (clubCount > bracket) {
        stages.push({ key: PRELIMINARY_STAGE, legs: 1, days: 1 });
    }
    let size = bracket;
    while (size >= 2) {
        const legs = size <= 16 && size > 2 ? 2 : 1;
        stages.push({ key: cupStageKey(size), legs, days: legs });
        size /= 2;
    }
    return stages;
}
/**
 * Reparte as rodadas entre as categorias, espalhando cada uma pela temporada.
 *
 * A cada rodada escolhe a categoria mais atrasada em relação à sua cota, o que
 * intercala liga, copa e continental em vez de amontoar um tipo no fim.
 */
function allocateRounds(total, quotas) {
    const keys = Object.keys(quotas);
    const assigned = {};
    for (const key of keys)
        assigned[key] = [];
    for (let round = 1; round <= total; round++) {
        let best = null;
        let bestDeficit = -Infinity;
        for (const key of keys) {
            const quota = quotas[key] ?? 0;
            const taken = assigned[key]?.length ?? 0;
            if (taken >= quota)
                continue;
            const deficit = (quota / total) * round - taken;
            if (deficit > bestDeficit) {
                bestDeficit = deficit;
                best = key;
            }
        }
        if (best)
            assigned[best]?.push(round);
    }
    return assigned;
}
export function buildCalendar(world, rng) {
    world.fixtures = {};
    world.standings = {};
    const countries = Object.values(world.countries);
    const leagues = countries.flatMap((country) => country.leagueIds.map((id) => getCompetition(world, id)));
    const cups = countries.map((country) => getCompetition(world, country.cupId));
    /*
     * Quantas rodadas a liga mais longa precisa, em turno e returno.
     *
     * Com número par de clubes são `n - 1` por turno, que é o caso de sempre.
     * Com número ímpar o método do círculo acrescenta um clube de mentira para
     * alguém folgar, e passam a ser `n` por turno: vinte e um clubes precisam de
     * vinte e uma rodadas em cada turno, e não de vinte.
     *
     * A conta antiga só valia para par, e a diferença não aparecia porque nenhum
     * banco tinha divisão ímpar. Com o clube criado pela pessoa entrando a mais,
     * a divisão passa a vinte e um e as duas últimas rodadas eram descartadas por
     * falta de dia: cada clube jogava 38 em vez de 40, calado.
     */
    const rodadasDaLiga = (clubes) => clubes < 2 ? 0 : (clubes % 2 === 1 ? clubes : clubes - 1) * 2;
    const leagueRounds = Math.max(0, ...leagues.map((league) => rodadasDaLiga(league.clubIds.length)));
    const cupStagesByCup = new Map(cups.map((cup) => [cup.id, buildCupStages(cup.clubIds.length)]));
    const cupDays = Math.max(0, ...[...cupStagesByCup.values()].map((stages) => stages.reduce((sum, stage) => sum + stage.days, 0)));
    // A continental só ocupa dias se o banco em uso tiver clubes para enchê-la.
    const continental = world.competitions[CONTINENTAL_ID];
    const knockoutStages = continental ? continentalKnockoutStages(world) : [];
    const continentalDays = knockoutStages.length === 0
        ? 0
        : GROUP_ROUNDS + knockoutStages.reduce((sum, stage) => sum + stage.days, 0);
    // Os estaduais abrem a temporada em bloco, antes de tudo. Espalhá-los pelo
    // ano como a liga e a copa seria infiel: no calendário de verdade eles
    // terminam justamente quando o nacional começa.
    const estadualDays = stateRounds(world);
    const estadualSlots = Array.from({ length: estadualDays }, (_, i) => i + 1);
    const total = estadualDays + leagueRounds + cupDays + continentalDays;
    const slots = allocateRounds(total - estadualDays, {
        league: leagueRounds,
        cup: cupDays,
        continental: continentalDays,
    });
    const deslocar = (lista) => lista.map((round) => round + estadualDays);
    const leagueSlots = deslocar(slots.league ?? []);
    const cupSlots = deslocar(slots.cup ?? []);
    const continentalSlots = deslocar(slots.continental ?? []);
    if (estadualDays > 0)
        buildStateFixtures(world, estadualSlots, rng.derive("estaduais"));
    // Ligas: cada uma usa os primeiros dias de liga de que precisa.
    for (const league of leagues) {
        world.standings[league.id] = emptyStandings(league.clubIds);
        league.champion = null;
        const schedule = doubleRoundRobin(league.clubIds, rng.derive("cal", league.id));
        schedule.forEach((pairs, index) => {
            const round = leagueSlots[index];
            if (round === undefined)
                return;
            for (const [homeId, awayId] of pairs) {
                addFixture(world, league.id, round, "liga", homeId, awayId, false);
            }
        });
    }
    // Copas nacionais: só as rodadas ficam marcadas agora. O sorteio de cada fase
    // acontece quando ela chega, porque depende de quem sobreviveu.
    for (const cup of cups) {
        const stages = cupStagesByCup.get(cup.id) ?? [];
        let cursor = 0;
        const stageList = stages.map((stage) => {
            const rounds = cupSlots.slice(cursor, cursor + stage.days);
            cursor += stage.days;
            return { key: stage.key, legs: stage.legs, rounds };
        });
        cup.champion = null;
        cup.cup = {
            stages: stageList,
            stageIndex: -1,
            survivors: rng.derive("cup", cup.id).shuffle([...cup.clubIds]),
            ties: [],
            byes: [],
        };
    }
    // Continental: os grupos são sorteados agora, porque os participantes já são
    // conhecidos, e o mata-mata só ganha confrontos depois da fase de grupos.
    if (continental && continentalDays > 0) {
        const groupRounds = continentalSlots.slice(0, GROUP_ROUNDS);
        let cursor = GROUP_ROUNDS;
        const stageList = knockoutStages.map((stage) => {
            const rounds = continentalSlots.slice(cursor, cursor + stage.days);
            cursor += stage.days;
            return { key: stage.key, legs: stage.legs, rounds };
        });
        buildContinentalGroups(world, groupRounds, stageList, rng.derive("continental"));
    }
    world.totalRounds = total;
    // A janela do meio do ano acompanha o tamanho do calendário. Presa em rodadas
    // fixas, ela abria no primeiro terço de uma temporada longa: a continental
    // esticou o ano de 48 para 61 rodadas e a janela de 18 a 22 deixou de ser o
    // meio de coisa nenhuma.
    const meio = Math.round(total / 2);
    world.settings.midSeasonWindow = [Math.max(1, meio - 2), Math.min(total, meio + 2)];
}
function addFixture(world, competitionId, round, stage, homeId, awayId, neutral) {
    const fixture = {
        id: nextId(world, "f"),
        competitionId,
        round,
        stage,
        homeId,
        awayId,
        played: false,
        homeGoals: null,
        awayGoals: null,
        shootoutWinner: null,
        neutral,
    };
    world.fixtures[fixture.id] = fixture;
}
//# sourceMappingURL=calendar.js.map